import type { IAuthUser } from "@somewear/auth";
import { AuthController, UserSource } from "@somewear/auth";
import { Sentry, setRedirectPath } from "@somewear/model";
import { selectOrganizationPickerOptions } from "@somewear/organization";
import type { SubscriptionState } from "@somewear/subscription";
import { OrgSelectForm, WorkspaceSelectForm } from "@somewear/ui";
import { selectActiveAuthId, selectIsInitialLoadComplete } from "@web/app/appSelectors";
import { useDocsRedirect } from "@web/app/docs/docs.hooks";
import {
	useActiveAccountChangeHandler,
	useLoginCallback,
	useLogoutCallback,
	useRegisterOnAuthServiceLoaded,
	useRegisterToAuthChanges,
	useWorkspaceTokenState,
} from "@web/app/hooks";
import type { RootState } from "@web/app/rootReducer";
import { useExternalAuth } from "@web/app/useExternalAuth";
import { TicketAuthStage, useTicketAuth } from "@web/app/useTicketAuth";
import Loader from "@web/common/Loader";
import * as React from "react";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation } from "react-router-dom";

import Config from "../config/Config";
import logo from "../img/newLogoAndText.svg";

const LandingRouter = React.lazy(() => import("../landing/LandingRouter"));

/**
 * Session gate for everything that isn't public: runs the auth listeners and
 * ticket/external auth flows, shows the loader until the session is settled,
 * then renders the app routes (<Outlet/>) for a fully-established session or
 * the landing experience (at the current URL) otherwise.
 */
const SessionLayout: React.FunctionComponent = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const appUser = useSelector((state: RootState) => state.app.user);
	const subscriptions = useSelector((state: SubscriptionState) => state.subscriptions);
	const isInitialLoadComplete = useSelector(selectIsInitialLoadComplete);
	const savedPath = useRef(location.pathname);
	const [workspaceToken, setWorkspaceToken] = useWorkspaceTokenState();
	const [authLoaded, setAuthLoaded] = useState(false);

	const activeAuthId = useSelector(selectActiveAuthId);

	// handlers that listen for the auth service to load
	const [authServiceLoaded, setAuthServiceLoaded] = useState(false);
	useRegisterOnAuthServiceLoaded(setAuthServiceLoaded);

	// Whether the user had an existing session when the page loaded (undefined = not yet known)
	const [hasSession, setHasSession] = useState<boolean | undefined>(undefined);

	// External auth callback flow (e.g. Beam CLI opening the web app with ?callback_port)
	const externalAuthRedirecting = useExternalAuth(authServiceLoaded, hasSession, activeAuthId);
	const docsRedirect = useDocsRedirect(authServiceLoaded, activeAuthId);

	// Ticket-based auth flow (SSH-safe alternative to callback_port)
	const ticketAuth = useTicketAuth(authServiceLoaded, hasSession, activeAuthId);
	const orgOptions = useSelector(selectOrganizationPickerOptions);
	const [selectedOrgId, setSelectedOrgId] = useState("");

	// handlers that listen for auth state changes
	const onAuthStateChangeLogout = useLogoutCallback(setAuthLoaded);

	const onAuthStateChangeLogin = useLoginCallback(workspaceToken, setWorkspaceToken);

	const onAuthStateChange = useCallback(
		(user: IAuthUser | undefined) => {
			setHasSession((prev) => prev ?? user !== undefined);
			AuthController.service.getUserIdToken$().subscribe((token) => {
				console.log(`on auth state change; hasToken: ${token !== undefined}`);
				const prevUid = UserSource.getInstance().getCurrentUser()?.id;
				if (user !== undefined) {
					if (prevUid !== user.id) {
						onAuthStateChangeLogin(user);
						setAuthLoaded(true);
					}
				} else if (prevUid !== undefined) {
					onAuthStateChangeLogout();
				} else if (token === undefined) {
					onAuthStateChangeLogout();
				}
			});
		},
		[onAuthStateChangeLogin, onAuthStateChangeLogout, setAuthLoaded],
	);

	// handler that listens for auth change events
	useRegisterToAuthChanges(
		authServiceLoaded,
		workspaceToken,
		setWorkspaceToken,
		onAuthStateChange,
	);

	// handlers that listen for org and workspace changes
	useActiveAccountChangeHandler(authServiceLoaded, setAuthLoaded, onAuthStateChange);

	// handler that listens for a saved path and stores it into redux
	useEffect(() => {
		dispatch(setRedirectPath(savedPath.current));
	}, [savedPath, dispatch]);

	const isSomewearCoUser = appUser?.email?.endsWith("@somewear.co");

	const isTestUser =
		appUser?.email?.includes("someweardev") || appUser?.email?.includes("someweartest");
	const phoneNotNeeded =
		appUser?.phone ||
		appUser?.hasWorkspace ||
		appUser?.hasOrganization ||
		isSomewearCoUser ||
		!Config.firebase.enable;
	const subscriptionNotNeeded =
		subscriptions.ids.isNotEmpty() ||
		appUser?.hasWorkspace ||
		appUser?.hasOrganization ||
		isSomewearCoUser ||
		!Config.firebase.enable;

	const currentAuthUser = AuthController.isServiceLoaded
		? AuthController.service.getCurrentAuthUser()
		: undefined;

	const isLoading = useMemo(() => {
		return !authLoaded || (!isInitialLoadComplete && currentAuthUser !== undefined);
	}, [authLoaded, isInitialLoadComplete, currentAuthUser]);

	// log the loading state each time it changes
	useEffect(() => {
		if (isLoading)
			console.log(
				`current user: ${currentAuthUser}; authLoaded: ${authLoaded}; isInitialLoadComplete: ${isInitialLoadComplete}`,
			);
	}, [isLoading, currentAuthUser, authLoaded, isInitialLoadComplete]);

	useEffect(() => {
		if (!isLoading) return;
		const timeout = setTimeout(() => {
			console.error("The app did not load within ten seconds");
			Sentry.captureException("The app did not load within ten seconds");
		}, 10000);

		return () => {
			clearTimeout(timeout);
		};
	}, [isLoading]);

	if (
		ticketAuth.stage !== TicketAuthStage.Idle &&
		ticketAuth.stage !== TicketAuthStage.Claiming
	) {
		return (
			<div className="fixed inset-0 flex flex-col bg-gradient-to-b from-[#010912] to-[#082845] dark">
				<img
					src="/img/graph.svg"
					className="fixed pointer-events-none bottom-0 left-0 max-h-[440px] min-w-full opacity-10 object-cover"
					alt=""
				/>
				<header className="flex items-center h-16 px-9 border-b border-stealth-border shrink-0">
					<img src={logo} alt="Somewear" className="h-16 w-52" />
				</header>
				<div className="flex flex-1 items-center justify-center overflow-auto p-8">
					{ticketAuth.stage === TicketAuthStage.SelectingOrg && (
						<div className="w-full max-w-sm">
							<h1 className="text-xl uppercase font-bold mb-2 tracking-wider text-center text-white">
								Choose Organization
							</h1>
							<p className="text-sm mb-8 text-center text-stealth-gray">
								Select the organization to connect this device to
							</p>
							<OrgSelectForm
								organizations={orgOptions.filter((o) => o.id !== "-1")}
								selectedOrgId={selectedOrgId}
								onSelectedOrgChange={setSelectedOrgId}
								onSubmit={(e) => {
									e.preventDefault();
									ticketAuth.onOrgSelected(selectedOrgId);
								}}
							/>
						</div>
					)}
					{ticketAuth.stage === TicketAuthStage.SelectingWorkspaces && (
						<div className="relative w-full max-w-2xl rounded-lg bg-stealth-panel p-8 border">
							<h1 className="text-xl uppercase font-bold mb-2 tracking-wider text-center text-white">
								Choose Workspaces
							</h1>
							<p className="text-sm mb-8 text-center text-stealth-gray">
								Select the workspaces this device can access
							</p>
							<WorkspaceSelectForm
								workspaces={ticketAuth.workspaces}
								onSubmit={ticketAuth.onWorkspacesSelected}
							/>
						</div>
					)}
					{ticketAuth.stage === TicketAuthStage.Done && (
						<div className="flex flex-col items-center gap-2">
							<h2 className="text-lg font-semibold text-white">Connected!</h2>
							<p className="text-sm text-stealth-gray">
								You can close this tab and return to your terminal.
							</p>
						</div>
					)}
					{ticketAuth.stage === TicketAuthStage.Expired && (
						<div className="flex flex-col items-center gap-2 text-center">
							<h2 className="text-lg font-semibold text-white">
								Sign-in link expired
							</h2>
							<p className="text-sm text-stealth-gray">
								Links are valid for 10 minutes and this one can't be reused.
							</p>
							<p className="text-sm text-stealth-gray">
								Go back to Beam and start the sign in again to get a new link, then
								close this tab.
							</p>
						</div>
					)}
					{ticketAuth.stage === TicketAuthStage.Error && (
						<div className="flex flex-col items-center gap-2">
							<h2 className="text-lg font-semibold text-white">
								Authorization failed
							</h2>
							<p className="text-sm text-stealth-gray">{ticketAuth.error}</p>
						</div>
					)}
				</div>
			</div>
		);
	}

	if (
		isLoading ||
		externalAuthRedirecting ||
		docsRedirect.isRedirecting ||
		ticketAuth.stage === TicketAuthStage.Claiming
	) {
		return (
			<div className="setup-background-content">
				<Loader fullScreen={true} />
			</div>
		);
	} else if (
		activeAuthId !== undefined &&
		(phoneNotNeeded || isTestUser || appUser?.isAnonymous) &&
		subscriptionNotNeeded
	) {
		return <Outlet />;
	} else {
		return (
			<Suspense fallback={<Loader fullScreen={true} />}>
				<LandingRouter />
			</Suspense>
		);
	}
};

export default SessionLayout;
